import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import SkeletonOne from "../../components/skeleton/SkeletonOne";
import { deleteGroupChat, groupChatList, leaveGroupChat, muteUnmuteGroupChat } from "../../redux/ApiSlice/MessageGroupSlice";

const DEFAULT_GROUP_IMAGE = "https://ui-avatars.com/api/?name=Group&background=random";

const MessageGroupList = ({ searchQuery = "" }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [openMenuId, setOpenMenuId] = useState(null);
    const menuRef = useRef(null);

    const { groupChatListData, loading } = useSelector((state) => state.messageGroup);
    // Local state for instant UI update
    const [localGroupList, setLocalGroupList] = useState([]);

    useOutsideClick(menuRef, () => setOpenMenuId(null));

    useEffect(() => {
        dispatch(groupChatList());
    }, [dispatch]);

    // Sync local state with redux data
    useEffect(() => {
        setLocalGroupList(groupChatListData || []);
    }, [groupChatListData]);

    // Filter groups based on search query
    const filteredGroupList = localGroupList.filter((group) => {
        if (!searchQuery.trim()) return true;
        return group.name.toLowerCase().includes(searchQuery.toLowerCase());
    });

    const handleLeaveGroup = (groupId) => {
        setLocalGroupList((prev) => prev.filter((g) => g.id !== groupId));
        dispatch(leaveGroupChat(groupId));
    }

    const handleDeleteGroup = (groupId) => {
        setLocalGroupList((prev) => prev.filter((g) => g.id !== groupId));
        dispatch(deleteGroupChat(groupId));
    }

    const handleMuteGroup = (groupId) => {
        dispatch(muteUnmuteGroupChat(groupId));
        setTimeout(() => {
            dispatch(groupChatList());
        }, 1000);
        setOpenMenuId(null);
    }

    return (
        <Fragment>
            {loading ? (
                <SkeletonOne />
            ) : filteredGroupList && filteredGroupList.length > 0 ? (
                filteredGroupList.map((group) => (
                    <div
                        key={group.id}
                        onClick={() => navigate(`/message/group/${group.id}`)}
                        className="px-2 py-1.5 bg-white dark:bg-black border-b border-gray-300 dark:border-gray-900 relative cursor-pointer"
                    >
                        <div className="flex flex-row w-full items-center">
                            <div className="flex-shrink-0 w-14 h-11 relative">
                                <img
                                    alt={group.name}
                                    src={group.image || DEFAULT_GROUP_IMAGE}
                                    className="h-11 w-11 rounded-full shadow-2xl object-cover border border-gray-300 dark:border-gray-700"
                                />
                                {/* Unread messages badge */}
                                {group.unread_messages > 0 && (
                                    <span className="absolute right-2 top-0 bg-red-600 text-white text-[10px] min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1 shadow">
                                        {group.unread_messages}
                                    </span>
                                )}
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                                <div className="flex items-start justify-between">
                                    <p className="font-semibold text-[13px] line-clamp-1 text-black dark:text-white truncate flex-1 mr-2">
                                        {group.name}
                                    </p>
                                    <div className="flex flex-col items-end relative flex-shrink-0">
                                        <p className="text-[10px] line-clamp-1 text-gray-500 dark:text-gray-700">
                                            {group.message_time ? group.message_time : "No time"}
                                        </p>
                                        <BiDotsHorizontalRounded
                                            className="absolute top-5 right-0 text-[20px] cursor-pointer text-gray-500 dark:text-gray-700"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setOpenMenuId(group.id);
                                            }}
                                        />
                                        {openMenuId === group.id && (
                                            <div
                                                ref={menuRef}
                                                className="absolute text-black dark:text-white bg-white dark:bg-gray-900 border border-gray-400 dark:border-gray-800 -top-1 right-0 rounded z-10"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <p
                                                    onClick={() => handleMuteGroup(group.id)}
                                                    className="text-[10px] px-2.5 py-0.5 border-b border-gray-300 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                >
                                                    {group.is_muted ? "Unmute" : "Mute"}
                                                </p>
                                                <p
                                                    onClick={() => handleLeaveGroup(group.id)}
                                                    className="text-[10px] px-2.5 py-0.5 border-b border-gray-300 dark:border-gray-800 hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                >
                                                    Leave
                                                </p>
                                                <p
                                                    onClick={() => handleDeleteGroup(group.id)}
                                                    className="text-[10px] px-2.5 py-0.5 border-none hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                                                >
                                                    Delete
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <p className="text-[12px] line-clamp-1 text-gray-700 dark:text-gray-400 truncate pr-8">
                                    {group.last_message ? group.last_message : "No messages yet."}
                                </p>
                            </div>
                        </div>
                    </div>
                ))
            ) : (
                <p className="text-[12px] text-center mt-5 text-gray-400 dark:text-white">
                    {searchQuery.trim() ? "No matching groups found" : "No Group Messages Found"}
                </p>
            )}
        </Fragment>
    );
};

export default MessageGroupList;